import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "@nexus/ui";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { EventDetailSection } from "./EventDetailSection";

/** Enough to recognise the shape of a payload without burying the section under it. */
const COLLAPSED_LINE_COUNT = 20;
const COPIED_RESET_MS = 2_000;

/**
 * A payload is debugging material, so all of it has to be reachable — but a
 * permissions array runs to thousands of lines, and pasting that straight into
 * the drawer pushed everything after it past the end of the scrollbar. So the
 * block opens at a readable height, grows on request, and never grows past the
 * viewport. Copy is what most of these end up wanting anyway.
 */
export function EventJsonSection({
  label,
  value,
}: {
  label: string;
  value: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const blockId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const { copied, copy } = useCopyToClipboard({ resetMs: COPIED_RESET_MS });

  const text = useMemo(() => JSON.stringify(value, null, 2), [value]);
  const lineCount = useMemo(() => text.split("\n").length, [text]);
  const isTruncated = lineCount > COLLAPSED_LINE_COUNT && !isExpanded;

  // Slicing the string rather than clipping with CSS keeps the long payloads
  // out of the DOM entirely until somebody asks for them.
  const shown = useMemo(
    () =>
      isTruncated
        ? text.split("\n").slice(0, COLLAPSED_LINE_COUNT).join("\n")
        : text,
    [text, isTruncated],
  );

  return (
    <EventDetailSection
      label={label}
      aside={
        <div className="flex items-center gap-1">
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {t("admin.events.drawer.jsonLines", { count: lineCount })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 rounded-sm text-[11px]"
            onClick={() => void copy(text)}
          >
            <Icon
              name={copied ? "check" : "copy"}
              size="xs"
              colorVar={copied ? "--success" : undefined}
              aria-hidden
            />
            {copied
              ? t("admin.events.drawer.copied", "Copied")
              : t("admin.events.drawer.copy", "Copy")}
          </Button>
        </div>
      }
    >
      <pre
        id={blockId}
        className="m-0 max-h-[60vh] overflow-auto rounded-md border border-border-subtle bg-muted px-4 py-3 font-mono text-[11px] leading-[1.6] text-foreground"
      >
        {shown}
        {isTruncated ? "\n…" : null}
      </pre>

      {lineCount > COLLAPSED_LINE_COUNT ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 self-start rounded-sm px-1.5 text-[11px]"
          aria-expanded={isExpanded}
          aria-controls={blockId}
          onClick={() => setIsExpanded((current) => !current)}
        >
          {isExpanded
            ? t("admin.events.drawer.showLess", "Show less")
            : t("admin.events.drawer.showAll", "Show all {{count}} lines", {
                count: lineCount,
              })}
        </Button>
      ) : null}
    </EventDetailSection>
  );
}
